import moment from "moment";
import { periodParams } from "@/pages/reports/components/report-period";

const PER_PAGE = 20;

export const joinFilter = (values) => (values && values.length ? values.join(",") : undefined);

export const splitFilter = (value, cast) =>
    value === undefined || value === null || value === ""
        ? []
        : String(value)
              .split(",")
              .filter(Boolean)
              .map(cast);

const emptyValue = (field) => {
    if (field.type === "period") return [];
    if (field.type === "range") return ["", ""];
    if (field.type === "checkbox") return false;
    if (field.type === "select") return field.multiple ? [] : undefined;
    return "";
};

export const emptyFiltersOf = (definitions) =>
    definitions.reduce((filters, field) => {
        filters[field.key] = emptyValue(field);
        return filters;
    }, {});

export const filtersFromQueryOf = (definitions, query) => {
    const read = (param) => query[`filter[${param}]`];
    return definitions.reduce((filters, field) => {
        if (field.type === "period") {
            const [from, to] = field.params.map(read);
            filters[field.key] = from && to ? [moment(from), moment(to)] : [];
        } else if (field.type === "range") {
            filters[field.key] = field.params.map((param) => read(param) || "");
        } else if (field.type === "checkbox") {
            filters[field.key] = read(field.param) === field.value;
        } else if (field.type === "select" && field.multiple) {
            filters[field.key] = splitFilter(read(field.param), field.numeric ? Number : String);
        } else if (field.type === "select") {
            const value = read(field.param);
            filters[field.key] = value === undefined ? undefined : field.numeric ? Number(value) : value;
        } else {
            filters[field.key] = read(field.param) || "";
        }
        return filters;
    }, {});
};

export const filterParamsOf = (definitions, applied) => {
    const number = (value) => String(value || "").replace(/\./g, "").replace(",", ".") || undefined;
    return definitions.reduce((params, field) => {
        const value = applied[field.key];
        const set = (param, paramValue) => {
            if (paramValue !== undefined) params[`filter[${param}]`] = paramValue;
        };
        if (field.type === "period") {
            const { from, to } = periodParams(value);
            set(field.params[0], from);
            set(field.params[1], to);
        } else if (field.type === "range") {
            set(field.params[0], number(value[0]));
            set(field.params[1], number(value[1]));
        } else if (field.type === "checkbox") {
            set(field.param, value ? field.value : undefined);
        } else if (field.type === "select" && field.multiple) {
            set(field.param, joinFilter(value));
        } else if (field.type === "select") {
            set(field.param, value === null ? undefined : value);
        } else {
            set(field.param, String(value || "").trim() || undefined);
        }
        return params;
    }, {});
};

export const filterFieldsOf = (definitions) =>
    definitions.map((field) => ({
        ...field,
        options: typeof field.options === "function" ? field.options() : field.options,
    }));

export const visitListMixin = {
    data() {
        return {
            filters: this.emptyFilters(),
            appliedFilters: this.emptyFilters(),
            options: { stores: [], providers: [], sellers: [] },
            rows: [],
            meta: { current_page: 1, total: 0 },
            perPage: PER_PAGE,
            sort: { key: null, dir: null },
            loading: false,
            requestSeq: 0,
        };
    },
    computed: {
        pagination() {
            const [singular, plural] = this.nouns;
            return {
                current: this.meta.current_page,
                pageSize: this.perPage,
                total: this.meta.total,
                showSizeChanger: !this.fixedPageSize,
                pageSizeOptions: ["20", "50", "100"],
                locale: { items_per_page: "/ página" },
                showTotal: (total) => `${total} ${total === 1 ? singular : plural}`,
            };
        },
        statusOptions() {
            return Object.keys(this.statusMap).map((value) => ({ value, label: this.statusMap[value].label }));
        },
    },
    mounted() {
        this.service
            .options()
            .then(({ data }) => {
                this.options = data;
            })
            .catch(() => {});
        this.load(this.readQuery(this.$route.query));
    },
    methods: {
        sortOrderFor(key) {
            return this.sort.key === key ? (this.sort.dir === "asc" ? "ascend" : "descend") : false;
        },
        emptyFilters() {
            return emptyFiltersOf(this.filterDefinitions());
        },
        filtersFromQuery(query) {
            return filtersFromQueryOf(this.filterDefinitions(), query);
        },
        filterParams(applied) {
            return filterParamsOf(this.filterDefinitions(), applied);
        },
        filterFields() {
            return filterFieldsOf(this.filterDefinitions());
        },
        readQuery(query) {
            const filters = this.filtersFromQuery(query);
            this.filters = filters;
            this.appliedFilters = { ...filters };
            this.perPage = Number(query.per_page) || PER_PAGE;
            this.sort = query.sort
                ? { key: String(query.sort).replace(/^-/, ""), dir: String(query.sort).startsWith("-") ? "desc" : "asc" }
                : { key: null, dir: null };
            return Number(query.page) || 1;
        },
        params(page) {
            return {
                page,
                per_page: this.fixedPageSize ? undefined : this.perPage,
                ...this.filterParams(this.appliedFilters),
                sort: this.sort.key ? `${this.sort.dir === "desc" ? "-" : ""}${this.sort.key}` : undefined,
            };
        },
        syncQuery(params) {
            const query = Object.keys(params).reduce((result, key) => {
                const value = params[key];
                if (value !== undefined) result[key] = Array.isArray(value) ? value.map(String) : String(value);
                return result;
            }, {});
            this.$router.replace({ query }).catch(() => {});
        },
        load(page) {
            const seq = ++this.requestSeq;
            const params = this.params(page);
            this.syncQuery(params);
            this.loading = true;
            this.service
                .list(params)
                .then(({ data }) => {
                    if (seq !== this.requestSeq) return;
                    this.rows = data.data;
                    this.meta = data.meta;
                    if (this.collectOptions) this.collectOptions(this.rows);
                    if (data.meta && data.meta.per_page) this.perPage = data.meta.per_page;
                })
                .catch((error) => {
                    if (seq !== this.requestSeq) return;
                    this.rows = [];
                    this.meta = { current_page: 1, total: 0 };
                    this.notifyError(error, this.loadError);
                })
                .finally(() => {
                    if (seq === this.requestSeq) this.loading = false;
                });
        },
        tableChange(pagination, _filters, sorter) {
            const key = sorter && sorter.order ? sorter.columnKey : null;
            const dir = sorter && sorter.order === "ascend" ? "asc" : "desc";
            const changed = key !== this.sort.key || (key && dir !== this.sort.dir) || pagination.pageSize !== this.perPage;
            this.sort = { key, dir: key ? dir : null };
            this.perPage = pagination.pageSize;
            this.load(changed ? 1 : pagination.current);
        },
        filtrar() {
            this.appliedFilters = { ...this.filters };
            this.load(1);
        },
        limpar() {
            this.filters = this.emptyFilters();
            this.appliedFilters = this.emptyFilters();
            this.sort = { key: null, dir: null };
            this.load(1);
        },
    },
};
